import Big from 'big.js'
import { useEffect, useMemo, useRef } from 'react'
import NumberFormat, { NumberFormatPropsBase, NumberFormatValues } from 'react-number-format'
import createDebugLogger from '../../../common/logger'
import { LimitedNumberFormatOptions, useNumberFormatter } from '../../../common/numberUtils'
import { useIntlContext } from '../../../components/IntlProviders.context'
import { useStateRef } from '../../../hooks/useStateRef'
import {
  forwardRef,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  useFormControlContext
} from '../../chakra'

const debug = createDebugLogger('InputNumber')

type NumberDisplayProps = {
  value?: string | number | null
  options?: LimitedNumberFormatOptions
}

type NumberRangeDisplayProps = {
  minValue?: string | number | null
  maxValue?: string | number | null
  options?: LimitedNumberFormatOptions
}

/**
 * NumberDisplay
 *
 * A component to display numbers. Values are formatted based on the user's locale,
 * and options with Intl.NumberFormat's options, barring currency or unit
 *
 * For Percent, a number between 1 and 0 is recommended
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-numberdisplay--docs
 */
export function NumberDisplay({ value, options }: NumberDisplayProps) {
  const numberLocaleFormatter = useNumberFormatter({
    ...options,
    // If the dev tells us to use a specific number of decimals, we'll use that (options.maximumFractionDigits)
    // Otherwise, we'll allow all the decimals
    maximumFractionDigits: options?.maximumFractionDigits ?? 20
  })
  const num = Number(value)
  if (value === '' || value == null || Number.isNaN(num)) return null
  return <>{numberLocaleFormatter.format(num)}</>
}

/**
 * NumberRangeDisplay
 *
 * A component to display a range of numbers. Values are formatted based on the user's locale,
 * and options with Intl.NumberFormat's options, barring currency or unit
 *
 * For Percent, a number between 1 and 0 is recommended
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-numberdisplay--docs
 */
export function NumberRangeDisplay({ minValue, maxValue, options }: NumberRangeDisplayProps) {
  const numberLocaleFormatter = useNumberFormatter({
    ...options,
    // If the dev tells us to use a specific number of decimals, we'll use that (options.maximumFractionDigits)
    // Otherwise, we'll allow all the decimals
    maximumFractionDigits: options?.maximumFractionDigits ?? 20
  })
  const min = Number(minValue)
  const max = Number(maxValue)
  if (minValue === '' || minValue == null || Number.isNaN(min)) return null
  if (maxValue === '' || maxValue == null || Number.isNaN(max))
    return <NumberDisplay value={minValue} options={options} />
  return <>{numberLocaleFormatter.formatRange(min, max)}</>
}

type onChangeFloat = (value: number | null) => void
type onChangeString = (value: string) => void
export type InputNumberProps<TEmitFloat extends boolean | undefined> = {
  min?: number
  max?: number
  formatOptions?: LimitedNumberFormatOptions
  'data-testid'?: string
} & Omit<NumberFormatPropsBase<InputProps>, 'onChange' | 'customInput' | 'prefix' | 'suffix' | 'displayType'> &
  // This code makes it so that if valueAsNumber is true,
  // then onChange must be a function that takes a number or null
  // and if valueAsNumber is false, then onChange must be a function that takes a string
  (TEmitFloat extends true
    ? { valueAsNumber: true; onChange?: onChangeFloat }
    : { valueAsNumber?: false; onChange?: onChangeString })

/**
 * InputNumber
 *
 * Form control input to enter numbers. Values are formatted based on the user's locale
 * and any configured options per Intl.NumberFormat, barring currency or unit.
 *
 * Values are displayed using the react-number-format component.
 *
 * For Currency Input, use InputCurrency
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-form-inputs-number--docs
 */
export const InputNumber = forwardRef<InputNumberProps<boolean | undefined>, 'input'>(
  (
    {
      value,
      defaultValue,
      min = -1e20,
      max = 1e20,
      'data-testid': dataTestId,
      onChange,
      onBlur,
      valueAsNumber,
      formatOptions,
      ...rest
    },
    ref
  ) => {
    const isControlled = value !== undefined

    debug('---- render ----', value)
    const { id, isDisabled } = useFormControlContext() ?? {}
    // We will store all three value types from react-number-format's onChange in state as they are all handy to have around
    const [values, setValues] = useStateRef<NumberFormatValues>(() => {
      return {
        value: '',
        floatValue: undefined,
        formattedValue: ''
      }
    })

    const resolvedOpts = useResolveNumberFormat(formatOptions)

    const numberFormatter = useNumberFormatter(formatOptions)

    const prevValuesRef = useRef<NumberFormatValues>(values.current)
    if (prevValuesRef.current.formattedValue !== values.current.formattedValue) {
      debug('values changed', { values: values.current, prevValues: prevValuesRef.current })
      prevValuesRef.current = values.current
    }

    // We'll keep track of the previous value, so we can compare it to the current value
    // and update the internal state if the value has changed. Using the usePrevious hook
    // didn't work here because I need to update the previous value sooner than the next render.
    const prevValueRef = useRef<string | number | null | undefined>()

    // We'll store the largest decimal scale seen since mount, so we can use it as the minimum decimal scale
    // Basically if a value/defaultValue is programmatically provided that has a higher precision than the
    // one from the Currency settings, we'll just honor it.
    const largestDecimalScaleSeenSinceMount = useRef(0)

    const truncatedValue = String(value || defaultValue || 0)
    const decimalsFromValue = Math.min(Number(truncatedValue.split('.')[1]?.length ?? 0), 20)
    const decimalScale = Math.max(
      decimalsFromValue,
      resolvedOpts.maximumFractionDigits ?? 0,
      largestDecimalScaleSeenSinceMount.current
    )
    largestDecimalScaleSeenSinceMount.current = decimalScale

    function makeValuesFromValue(v: string | number | null | undefined, clamp = false) {
      if (v === '' || v == null) {
        return {
          value: '',
          floatValue: undefined,
          formattedValue: ''
        }
      }
      // Restrict the value to the min/max props
      let num = Number(v)
      if (clamp) {
        num = Math.min(Math.max(num, min), max)
      }
      return {
        value:
          String(num).split('.')[1]?.length < (resolvedOpts.minimumFractionDigits ?? 0)
            ? num.toFixed(resolvedOpts.minimumFractionDigits)
            : String(num),
        floatValue: num,
        formattedValue: formatNumberWithoutPercentSign(num)
      }

      // formatNumberWithoutPercentSign: excludes the percent sign
      //    because it's displayed as a prefix or suffix using InputLeftElement or InputRightElement
      function formatNumberWithoutPercentSign(v: string | number | null | undefined) {
        const num = Number(v)
        if (v === '' || v == null || Number.isNaN(num)) return ''
        debug('formatNumberWithoutPercentSign', id, v)
        return numberFormatter
          .format(formatOptions?.style === 'percent' ? Big(num).div(100).toNumber() : num)
          .replace(resolvedOpts.percentSignSymbol, '')
          .trim()
      }
    }

    // If the value prop (aka externally controlled value) has changed, we need to update the internal state
    const controlledValueChanged = value !== prevValueRef.current
    const valueIsDifferentFromStoredValue = valueAsNumber
      ? value !== values.current.floatValue
      : value !== values.current.value
    if (isControlled && controlledValueChanged && valueIsDifferentFromStoredValue) {
      // We'll have to construct the new values manually in a shape similar to the NumberFormatValues from react-number-format
      const newValues = makeValuesFromValue(value)
      debug('value HasChanged', id, {
        value,
        prevValue: prevValueRef.current,
        outOfSync: valueIsDifferentFromStoredValue,
        prevValues: values.current,
        newValues
      })
      setValues(newValues)
      prevValueRef.current = value
    }

    useEffect(() => {
      if (defaultValue !== undefined) {
        const values = makeValuesFromValue(defaultValue)
        debug('defaultValue set into values', id, values)
        setValues(values)
      }
    }, [])

    function internalOnChange(newValues: NumberFormatValues) {
      debug('internalOnChange', id, newValues, { isControlled })

      // If uncontrolled, update the internal state
      if (!isControlled) setValues(newValues)

      // Either way, call the onChange callback with the value
      if (valueAsNumber) {
        ;(onChange as onChangeFloat)?.(newValues.floatValue ?? null)
      } else {
        ;(onChange as onChangeString)?.(newValues.value)
      }
    }

    return (
      <InputGroup>
        {resolvedOpts.style === 'percent' && resolvedOpts.percentSignPosition === 'prefix' && (
          <InputLeftElement data-currency-code pointerEvents="none" pl={4} opacity={isDisabled ? '0.4' : '1'}>
            {resolvedOpts.percentSignSymbol}
          </InputLeftElement>
        )}
        {/* @ts-ignore */}
        <NumberFormat
          id={id}
          value={values.current.formattedValue}
          customInput={Input}
          getInputRef={ref}
          data-testid={dataTestId}
          decimalSeparator={resolvedOpts.decimalDelimiter}
          thousandSeparator={resolvedOpts.groupDelimiter}
          decimalScale={decimalScale}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            // onBlur if you've only typed, e.g. 55.1 then the value is changed to 55.10
            // We do leave empty string as empty string though, we don't want to format to 0.00 for empty.
            const newValues = makeValuesFromValue(values.current.value, true)
            debug('onBlur', id, values.current.value, '->', newValues)
            internalOnChange(newValues)
            onBlur?.(e)
          }}
          onValueChange={(values, sourceInfo) => {
            debug('onValueChange', id, values, sourceInfo.source)
            // While actively typing we allow invalid partial entries such as 55.1
            // which is missing another decimal place. But the user is still typing.
            // We also do not clamp, aka we don't use makeValuesFromValue, during
            // onValueChange, we wait until blur to clamp if needed.
            internalOnChange(values)
          }}
          {...rest}
        />
        {resolvedOpts.style === 'percent' && resolvedOpts.percentSignPosition === 'suffix' && (
          <InputRightElement data-currency-code pointerEvents="none" pr={4} opacity={isDisabled ? '0.4' : '1'}>
            {resolvedOpts.percentSignSymbol}
          </InputRightElement>
        )}
      </InputGroup>
    )
  }
)

InputNumber.displayName = 'InputNumber'

/**
 * Hook to return all the parts of the number formats needed to display in an input
 *
 * {
 *   'locale': 'en-US',
 *   'numberingSystem': 'latn',
 *   'style': 'decimal',
 *   'minimumIntegerDigits': 1,
 *   'minimumFractionDigits': 2,
 *   'maximumFractionDigits': 2,
 *   'useGrouping': 'auto',
 *   'groupDelimiter': '.',
 *   'decimalDelimiter': ',',
 *   'notation': "standard",
 *   'roundingIncrement': 1
 *   'roundingMode': "halfExpand"
 *   'roundingPriority': "auto"
 *   'trailingZeroDisplay':"auto"
 * }
 */
export const useResolveNumberFormat = (options: LimitedNumberFormatOptions = {}) => {
  const intl = useIntlContext().intl

  return useMemo(() => {
    const numberFormat = new Intl.NumberFormat(intl.locale, options)

    // Just give it a number that needs grouping and decimal formatting, so we can find out which
    // characters are used for grouping and decimal.
    const parts = numberFormat.formatToParts(Number(11111111.11111))
    const resolvedOptions = numberFormat.resolvedOptions()

    const groupDelimiter = getPart(parts, 'group')
    let decimalDelimiter = getPart(parts, 'decimal')

    // if the decimal places allowed for given currency is zero, the decimal delimiter
    // must not be set to the same value as the group delimiter or undefined. Otherwise,
    // the react-number-format library will throw an error. The code below is ensuring
    // that it is set to a different value.
    if (resolvedOptions.maximumFractionDigits === 0) {
      if (groupDelimiter === ',') {
        decimalDelimiter = '.'
      } else {
        decimalDelimiter = ','
      }
    }

    let percentSignSymbol = ''
    let percentSignIndex = -1
    let percentSignPosition = ''

    if (options.style === 'percent') {
      percentSignSymbol = getPart(parts, 'percentSign') ?? ''
      percentSignIndex = parts.findIndex(part => part.type === 'percentSign')
      percentSignPosition = percentSignIndex <= 1 ? 'prefix' : 'suffix'
    }

    return {
      ...resolvedOptions,
      groupDelimiter,
      decimalDelimiter,
      percentSignSymbol,
      percentSignIndex,
      percentSignPosition
    }
  }, [options])
}

function getPart(parts: Intl.NumberFormatPart[], name: Intl.NumberFormatPart['type']): string | undefined {
  return parts.find(part => part.type === name)?.value
}
